import { takeoverKind } from './clientTakeover.js';

/**
 * Client dashboard "Portfolio Overview" chart: daily JTNC P&L from the client's own
 * completed trades. Trades the client managed themselves (closed our position, or our
 * entry was rejected for buying power), desk test orders and rows flagged off the
 * desk's list are left out — the chart shows only what JTNC traded.
 */

const nyDate = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/New_York',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const round2 = (n) => Math.round(n * 100) / 100;

const toDate = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

const normalizeSymbol = (s) => {
  const sym = String(s || '').trim().toUpperCase().replace(/^\$+/, '');
  return sym === 'SPXW' ? 'SPX' : sym;
};

// Tastytrade rows closed by the client carry it in the exit reason, e.g.
// 'manual - closed by the account holder at Tastytrade'.
const CLIENT_CLOSED_REASON = /account holder|client closed/i;

/** Why a completed backlog row is not JTNC P&L, or null when it is. */
export const nonJtncReason = (raw) => {
  if (takeoverKind(raw)) return 'client_managed';
  if (CLIENT_CLOSED_REASON.test(String(raw?.exit?.reason || ''))) return 'client_managed';
  // The platform never saw our exit, so the position was closed outside it.
  if (raw?.exit_price_unavailable === true) return 'client_managed';
  if (raw?.test_trade === true) return 'test';
  if (raw?.jtnc_pnl_excluded === true) return 'not_on_desk_list';
  return null;
};

/** [{ date: 'YYYY-MM-DD' (ET), pnl, bySymbol: { SPY, QQQ, SPX } }] in date order. */
export function jtncDailyPnlFromBacklogs(rows) {
  const days = new Map();
  for (const raw of rows || []) {
    if (raw?.tradeType !== 'completed') continue;
    if (nonJtncReason(raw)) continue;
    const pnl = Number(raw?.performance?.netPnl);
    if (!Number.isFinite(pnl)) continue;
    const exitAt = toDate(raw?.exit?.filledAt);
    if (!exitAt) continue;
    const date = nyDate.format(exitAt);
    if (!days.has(date)) days.set(date, { date, pnl: 0, bySymbol: {} });
    const day = days.get(date);
    const symbol = normalizeSymbol(raw.symbol);
    day.pnl += pnl;
    day.bySymbol[symbol] = (day.bySymbol[symbol] || 0) + pnl;
  }
  return [...days.values()]
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((d) => ({
      date: d.date,
      pnl: round2(d.pnl),
      bySymbol: Object.fromEntries(Object.entries(d.bySymbol).map(([k, v]) => [k, round2(v)])),
    }));
}
